import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChange,
} from '@angular/core';
import { List } from 'immutable';
import { Subject } from 'rxjs';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import { Canvas } from '../../../features-data/sub-models/canvas.model';
import { NavigationService } from '../../../navigation/navigation.service';
import { ICheckmarkItem } from '../../../shared/components/checkmark-list/checkmark-list.component';
import { IRouter } from '../../../shared/interfaces/router.interface';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { GrowthDialogBuilder, ICanvasData } from './growth-dialog.builder';
import { IGrowthDialogData } from './growth-dialog.interface';
import { filter, takeUntil } from 'rxjs/operators';
import {
  OPT_OUT_TRIAL_CANVAS_TYPE,
  START_TRIAL_ACTION_ID,
} from './growth-dialog.constant';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  buildOptOutTrialCanvasTrackingObject,
  TrackingObjectBuilder,
} from '../../../tracking/tracking-object.builder';
import { CtaPlan } from '../../../features-data/sub-models/cta-plan';
import { IconSizesEnum, IIcon } from 'i2g-ng-ui';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';

@Component({
  selector: 'app-growth-dialog',
  templateUrl: './growth-dialog.component.html',
  styleUrls: ['./growth-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [GrowthDialogBuilder],
})
export class GrowthDialogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() canvas: Canvas;

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _clearDataTimeout: number;

  dialogType: string;
  imgSrc: string;
  headerLabel: string;
  planLevel: string;
  subtitle: string;
  bodyText: string;
  growthHighlights: List<ICheckmarkItem>;
  primaryActionText: string;
  primaryActionRoute: IRouter;
  primaryActionCallback: Function;
  secondaryActionText: string;
  secondaryActionRoute: IRouter;
  secondaryActionCallback: Function;
  actionLeadText: string;
  dismissible: boolean;
  footerText: string;
  primaryActionTrackingAdditionalProperties: any;
  secondaryActionTrackingAdditionalProperties: any;
  trackingObject: ITrackingObject;
  trackingScreenName: TrackingScreenNamesEnum;
  primaryActionTapped: ITrackingAction;
  isColumnLayout: boolean;
  ctaPlans: List<CtaPlan>;
  selectedPlan: CtaPlan;
  selectedIcon: IIcon;

  constructor(
    private _navigationService: NavigationService,
    private _growthDialogBuilder: GrowthDialogBuilder,
    private _dialogService: DialogService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingDispatcher: TrackingDispatcher,
    private _trackingObjectBuilder: TrackingObjectBuilder
  ) {}

  ngOnChanges(changes) {
    const canvasChanges: SimpleChange = changes.canvas;
    const canvas: Canvas = canvasChanges.currentValue || this.canvas;

    if (canvas) {
      const canvasData: ICanvasData = this._growthDialogBuilder.buildDialogData(
        canvas
      );

      this.dialogType = canvas.name;
      this.imgSrc = canvasData.imgSrc;
      this.headerLabel = canvasData.headerLabel;
      this.subtitle = canvasData.subtitle;
      this.planLevel = canvasData.planLevel;
      this.bodyText = canvasData.bodyText;
      this.growthHighlights = canvasData.growthHighlights;
      this.primaryActionText = canvasData.primaryActionText;
      this.primaryActionRoute = canvasData.primaryActionRoute;
      this.secondaryActionText = canvasData.secondaryActionText;
      this.secondaryActionRoute = canvasData.secondaryActionRoute;
      this.footerText = canvasData.footerText;
      this.actionLeadText = canvasData.actionLeadText;
      this.dismissible = canvasData.dismissible;
      this.trackingObject = canvasData.trackingObject;
      this.trackingScreenName = canvasData.trackingScreenName;
      this.ctaPlans = canvasData.ctaPlans;
      this.primaryActionTrackingAdditionalProperties =
        canvasData.primaryActionTrackingAdditionalProperties;
      this.secondaryActionTrackingAdditionalProperties =
        canvasData.secondaryActionTrackingAdditionalProperties;

      this.isColumnLayout = canvas.type === OPT_OUT_TRIAL_CANVAS_TYPE;
      if (this.isColumnLayout) {
        this.trackingScreenName =
          TrackingScreenNamesEnum.opt_out_trial_landing_page;
        this.primaryActionTapped = this._trackingActionBuilder.buildButtonTapped(
          START_TRIAL_ACTION_ID,
          this.trackingScreenName
        );
        this.trackingObject = buildOptOutTrialCanvasTrackingObject(
          canvasData.trackingObject.properties
        );
      }

      if (!this.ctaPlans.isEmpty()) {
        this.selectedIcon = {
          name: 'filled-circle-checkmark',
          size: IconSizesEnum.medium,
        };
        this.selectPlan(this.ctaPlans.get(0), false);
      }
    }
  }

  ngOnInit() {
    this._dialogService.dialogTriggers$
      .pipe(
        takeUntil(this._destroyed),
        filter((dialogTrigger: IDialogTrigger) => {
          return (
            dialogTrigger.type === this.dialogType &&
            dialogTrigger.action === DialogActionsEnum.open
          );
        })
      )
      .subscribe((dialogTrigger: IDialogTrigger) => {
        const dialogData: IGrowthDialogData = dialogTrigger.data;

        this.primaryActionCallback = dialogData.primaryActionCallback;
        this.secondaryActionCallback = dialogData.secondaryActionCallback;
      });
  }

  selectPlan(plan: CtaPlan, shouldTrack = true) {
    this.selectedPlan = plan;
    this.primaryActionRoute = this._growthDialogBuilder.buildRouteFromUri(
      plan.uri
    );
    this.headerLabel = plan.pageTitle;

    this.primaryActionTrackingAdditionalProperties.target_plan_uuid =
      plan.planUuid;
    this.primaryActionTrackingAdditionalProperties.plan_duration_selected =
      plan.durationUnit;

    if (shouldTrack) {
      this.trackPlanSelection(plan);
    }
  }

  trackPlanSelection(plan: CtaPlan) {
    const radioButtonTapped = this._trackingActionBuilder.buildButtonTapped(
      plan.durationUnit,
      this.trackingScreenName
    );
    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      radioButtonTapped,
      this.primaryActionTrackingAdditionalProperties
    );
  }

  onPrimaryAction() {
    const primaryActionRoute = this.primaryActionRoute;

    if (this.primaryActionCallback) {
      this.primaryActionCallback();

      if (primaryActionRoute) {
        setTimeout(() => this.navigateToCta(primaryActionRoute));
      }
    } else if (primaryActionRoute) {
      this.navigateToCta(primaryActionRoute);
    }
  }

  onSecondaryAction() {
    const secondaryActionRoute = this.secondaryActionRoute;

    if (this.secondaryActionCallback) {
      this.secondaryActionCallback();

      if (secondaryActionRoute) {
        setTimeout(() => this.navigateToCta(secondaryActionRoute));
      }
    } else if (secondaryActionRoute) {
      this.navigateToCta(secondaryActionRoute);
    }
  }

  navigateToCta(routeInfo: IRouter) {
    if (routeInfo.external) {
      this._navigationService.goToExternalUrl(routeInfo.route, true);
    } else {
      this._navigationService.goTo(routeInfo.route, routeInfo.navigationExtras);
    }
  }

  ngOnDestroy() {
    clearTimeout(this._clearDataTimeout);

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
